import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import {
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useState
} from 'react'
import { themeClass } from '~/styles/theme.css'
import { HoverInfoContext } from '~/utils/contextes'
import { wrapper } from './HoverInfo.css'

type HoverInfoProps = {
  linkedRef: HTMLElement | null
  onClose?: () => void
  delay?: number
} & PropsWithChildren

export const HoverInfo = ({
  linkedRef,
  onClose,
  delay = 500,
  children
}: HoverInfoProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)

  const middleware = [offset(10), flip(), shift({ padding: 5 })]

  const { x, y, strategy, refs, context } = useFloating({
    placement: 'right-start',
    open: isOpen,
    onOpenChange: (isOpen) => {
      setIsOpen(isOpen)
      if (!isOpen && onClose) {
        onClose()
      }
    },
    middleware,
    whileElementsMounted: autoUpdate
  })

  useEffect(() => {
    if (linkedRef) {
      refs.setReference(linkedRef)
    }
  }, [linkedRef, refs])

  const hover = useHover(context, {
    move: false,
    delay: { open: delay, close: 100 }
  })

  const { getFloatingProps } = useInteractions([hover])

  return (
    <FloatingPortal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="HoverInfo"
            ref={refs.setFloating}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            className={clsx(themeClass, wrapper)}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0
            }}
            {...getFloatingProps()}
          >
            <HoverInfoContext.Provider
              value={{ close: () => context.onOpenChange(false) }}
            >
              {children}
            </HoverInfoContext.Provider>
          </motion.div>
        )}
      </AnimatePresence>
    </FloatingPortal>
  )
}
